<template>
  <div ref="mediaWrapper" class="secured-media-wrapper" :class="['type-' + mediaType, { 'has-loaded': hasLoaded }]" @click="$emit('click')">
    <div v-if="isLoading" class="secured-media-loader d-flex justify-center align-center">
      <v-progress-circular indeterminate color="#58d388" size="22" width="2"></v-progress-circular>
    </div>

    <div v-else-if="!hasBeenVisible" class="secured-media-loader d-flex justify-center align-center">
      <v-icon color="rgba(255,255,255,0.3)">mdi-eye-off-outline</v-icon>
    </div>

    <img v-else-if="mediaType === 'image' && blobUrl" :src="blobUrl" class="secured-media-content" />

    <video v-else-if="mediaType === 'video' && blobUrl" :src="blobUrl" controls class="secured-media-content"></video>

    <audio v-else-if="mediaType === 'audio' && blobUrl" :src="blobUrl" controls class="secured-media-content audio"></audio>

    <iframe v-else-if="mediaType === 'pdf' && blobUrl" :src="blobUrl" class="secured-media-content pdf"></iframe>

    <a v-else-if="blobUrl" :href="blobUrl" :download="downloadName || 'datei'" class="secured-media-download">
      <v-icon left small>mdi-download</v-icon> Herunterladen
    </a>

    <div v-else-if="hasLoaded && !blobUrl" class="secured-media-error">
      <v-icon small color="red lighten-2" class="mr-1">mdi-alert-circle</v-icon>
      <span class="caption red--text text--lighten-2">Fehler</span>
    </div>
  </div>
</template>

<script>
import axios from "axios"

const mediaCache = new Map()

function buildHeaders() {
  return { Authorization: localStorage.getItem("token") }
}

export default {
  name: "SecuredMedia",
  props: {
    // Voll qualifizierter API-Pfad ab "/api/akademie/..." (ohne Token-Query)
    src: { type: String, required: true },
    mediaType: { type: String, default: "image" }, // image | video | audio | pdf | file
    downloadName: { type: String, default: null },
    eager: { type: Boolean, default: false }, // ignoriert IntersectionObserver
    cacheKey: { type: String, default: null }, // optional eigener Cache-Key
  },
  data() {
    return {
      isLoading: false,
      blobUrl: null,
      hasBeenVisible: false,
      hasLoaded: false,
      observer: null,
    }
  },
  computed: {
    effectiveCacheKey() {
      return this.cacheKey || this.src
    },
  },
  watch: {
    src() {
      this.reset()
      if (this.eager) this.loadMedia()
    },
  },
  mounted() {
    if (this.eager) {
      this.hasBeenVisible = true
      this.loadMedia()
      return
    }
    this.createObserver()
  },
  beforeDestroy() {
    this.disconnectObserver()
    if (this.blobUrl) URL.revokeObjectURL(this.blobUrl)
  },
  methods: {
    reset() {
      if (this.blobUrl) {
        URL.revokeObjectURL(this.blobUrl)
        this.blobUrl = null
      }
      this.isLoading = false
      this.hasLoaded = false
      this.hasBeenVisible = !!this.eager
    },
    disconnectObserver() {
      if (this.observer) {
        this.observer.disconnect()
        this.observer = null
      }
    },
    createObserver() {
      if (this.hasBeenVisible) return
      if (!this.$refs.mediaWrapper) {
        setTimeout(this.createObserver, 80)
        return
      }
      this.observer = new IntersectionObserver(this.handleIntersect, { threshold: 0.01, rootMargin: "100px" })
      this.observer.observe(this.$refs.mediaWrapper)
    },
    handleIntersect(entries) {
      if (entries[0].isIntersecting && !this.hasBeenVisible) {
        this.hasBeenVisible = true
        this.loadMedia()
        this.disconnectObserver()
      }
    },
    async loadMedia() {
      if (!this.src || this.hasLoaded) return
      const key = this.effectiveCacheKey
      const cached = mediaCache.get(key)
      if (cached) {
        this.blobUrl = URL.createObjectURL(cached)
        this.hasLoaded = true
        return
      }
      this.isLoading = true
      try {
        const res = await axios.get(this.src, { responseType: "blob", headers: buildHeaders() })
        let blob = res.data
        if (!(blob instanceof Blob)) blob = new Blob([res.data], { type: res.headers["content-type"] })
        mediaCache.set(key, blob)
        this.blobUrl = URL.createObjectURL(blob)
      } catch (err) {
        console.error("[SecuredMedia] load error", err)
      } finally {
        this.isLoading = false
        this.hasLoaded = true
      }
    },
  },
}
</script>

<style scoped>
.secured-media-wrapper {
  position: relative;
  display: block;
  width: 100%;
  min-height: 80px;
}
.secured-media-content {
  display: block;
  max-width: 100%;
  max-height: 100%;
  width: 100%;
  border-radius: 8px;
}
.secured-media-content.pdf {
  height: 70vh;
  border: 0;
}
.secured-media-content.audio {
  height: 40px;
}
.secured-media-loader,
.secured-media-error {
  width: 100%;
  min-height: 80px;
  background: rgba(0, 0, 0, 0.25);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.secured-media-download {
  display: inline-flex;
  align-items: center;
  color: #58d388;
  text-decoration: none;
  padding: 8px 12px;
  background: rgba(0, 150, 64, 0.1);
  border-radius: 8px;
}
</style>
